/* eslint-disable max-lines-per-function */
import { GripVertical } from 'lucide-react';
import { Reorder } from 'framer-motion';
import { useContext, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge, Box, Button, HStack, Modal, Text } from '@rtaoutdoorlivingllc/rta-ui';

import { DesignIndexEntry } from '@rta/rta-blueprint-library';

import { DESIGN_PAGE_SIZE } from '../../config';
import { useKeystoneSession } from '../../auth/use-keystone-session';
import { AppContext, DesignActionTypes } from '@context';
import { getCanonicalDesignId } from '@helpers';
import { lang } from '@lang';
import { patchBlueprintMetadata } from '@external';
import { selectNavigation, useAppSelector } from '@state';
import { useAppToast } from '@hooks';

export type ManageDesignsModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

type DesignRowProps = {
	entry: DesignIndexEntry;
	position: number;
	isLoaded: boolean;
	isCurrent: boolean;
	isLastToLoad: boolean;
	onOpen: (designId: string) => void;
};

const DesignRow = (props: DesignRowProps): ReactElement => {
	const { entry, position, isLoaded, isCurrent, isLastToLoad, onOpen } = props;
	const pressedAt = useRef<{ x: number, y: number } | null>(null);

	// a drag ends in a click, so the press has to have gone nowhere for it to count as opening the design
	const handleClick = (x: number, y: number): void => {
		const start = pressedAt.current;
		if (start && Math.hypot(x - start.x, y - start.y) > 4) return;
		onOpen(entry.id);
	};

	return (
		<Reorder.Item
			value={entry}
			whileDrag={{ scale: 1.01, zIndex: 1 }}
			style={{ position: 'relative', listStyle: 'none', cursor: 'grab' }}
		>
			<HStack
				spacing={'10px'}
				px={'10px'}
				py={'7px'}
				my={'3px'}
				bg={'rta.surface'}
				border={'1px solid'}
				borderColor={isCurrent ? 'rta.accent' : 'rta.border'}
				borderRadius={'rta-sm'}
				borderBottomWidth={isLastToLoad ? '3px' : '1px'}
				borderBottomColor={isLastToLoad ? 'rta.accent' : undefined}
				_active={{ cursor: 'grabbing' }}
				onPointerDown={(event) => { pressedAt.current = { x: event.clientX, y: event.clientY }; }}
			>
				<Box color={'rta.textFaint'} flexShrink={0}><GripVertical size={16}/></Box>
				<Badge tone={'neutral'}>{position + 1}</Badge>
				<Text
					flex={'1'}
					minW={0}
					fontSize={'rta-body'}
					noOfLines={1}
					color={isLoaded ? 'rta.text' : 'rta.textFaint'}
					fontWeight={isCurrent ? 600 : 400}
					title={isLoaded ? 'Open this design' : lang.customer.tooltip.designNotLoaded}
					_hover={isLoaded ? { textDecoration: 'underline' } : undefined}
					onClick={(event) => handleClick(event.clientX, event.clientY)}
				>
					{(entry.description ?? '') === '' ? 'no description' : entry.description}
				</Text>
				{entry.favorite && <Badge tone={'accent'}>favorite</Badge>}
			</HStack>
		</Reorder.Item>
	);
};

export const ManageDesignsModal = (props: ManageDesignsModalProps): ReactElement => {
	const { isOpen, onClose } = props;

	const { appState: { customerDesigns }, appDispatch } = useContext(AppContext);
	const { getToken } = useKeystoneSession();
	const appToast = useAppToast();
	const appNav = useAppSelector(selectNavigation);
	const navigate = useNavigate();

	const { email, index } = customerDesigns;
	const [order, setOrder] = useState<Array<DesignIndexEntry>>(index);
	const [isSaving, setIsSaving] = useState(false);

	// only a loaded design can be opened — selecting one that is still unloaded would navigate nowhere
	const loaded = useMemo(() => new Map(customerDesigns.designs.map((design) => {
		const kitchen = design.blueprint.kitchens.getActiveVersion();
		return [design.blueprint.id, { version: kitchen?.version ?? null, baseVersion: kitchen?.baseVersion ?? null }];
	})), [customerDesigns.designs]);

	// the index is the saved order — reopening the modal discards an unsaved shuffle
	useEffect(() => {
		if (isOpen) setOrder(index);
	}, [isOpen, index]);

	const handleOpen = (designId: string): void => {
		const target = loaded.get(designId);
		if (!target) return;

		const { version, baseVersion } = target;
		navigate(`/r/${appNav.email}/${designId}${version ? `/${version}` : `/${baseVersion ?? ''}?draft`}`);
		onClose();
	};

	const handleSave = async (): Promise<void> => {
		if (!email) return;

		// only the designs that load first carry a manual rank; everything below falls back to newest-first
		const priorities: Record<string, number> = {};
		order.forEach((entry, position) => {
			priorities[entry.id] = position < DESIGN_PAGE_SIZE ? position + 1 : 0;
		});

		const changed = order.filter((entry) => (entry.priority ?? 0) !== priorities[entry.id]);

		if (changed.length === 0) {
			onClose();
			return;
		}

		setIsSaving(true);

		const results = await Promise.all(changed.map((entry) => patchBlueprintMetadata({
			customerEmail: email,
			designId: getCanonicalDesignId(entry),
			metadata: { priority: priorities[entry.id] },
			getToken,
			mode: 'live'
		})));

		setIsSaving(false);

		if (results.some(([saved]) => !saved)) {
			appToast.error({ title: lang.customer.message.designOrderFailed });
			return;
		}

		appDispatch({ type: DesignActionTypes.setDesignPriorities, payload: priorities });
		appToast.success({ title: lang.customer.message.designOrderSaved });
		onClose();
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={lang.customer.label.manageDesigns}
			subtitle={lang.customer.message.designOrderHelp}
			width={640}
			footer={<>
				<Button variant={'secondary'} onClick={onClose}>Cancel</Button>
				<Button variant={'primary'} isLoading={isSaving} isDisabled={order.length === 0} onClick={handleSave}>Save Order</Button>
			</>}
		>
			{order.length === 0
				? <Text fontSize={'rta-body'} color={'rta.textMuted'}>{lang.customer.message.noDesignsToOrder}</Text>
				: <Reorder.Group axis={'y'} values={order} onReorder={setOrder} style={{ margin: 0, padding: 0 }}>
					{order.map((entry, position) => <DesignRow
						key={entry.id}
						entry={entry}
						position={position}
						isLoaded={loaded.has(entry.id)}
						isCurrent={entry.id === appNav.designId}
						isLastToLoad={position === DESIGN_PAGE_SIZE - 1 && position !== order.length - 1}
						onOpen={handleOpen}/>)}
				</Reorder.Group>}
		</Modal>
	);
};
